import React from 'react';
import { CJEvent } from '@teetsh/app/src/types/event';
import { HoraireComponent } from 'containers/CahierJournal/DayColumn/Seance/Horaire';
import { CJEventBody } from 'containers/CahierJournal/DayColumn/Seance/CJEventBody';
import { BOLabels } from '@teetsh/app/src/types/user';
import {
  bgColors,
  getTextColorFromBackgroundColor,
} from '@teetsh/app/src/components/colors';
import tw from 'twin.macro';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { ClassroomsOrg } from '@teetsh/app/src/types/school';
import { useFieldsToDisplay } from 'containers/CahierJournal/Toolbar/Filters/FieldsToDisplay/atom';
import { useEventCustomFieldDefinitionsContext } from 'containers/App/context/EventCustomFieldDefinitionsContext/EventCustomFieldDefinitionsProvider';
import { getDurationInHoursAndMinutesBetween } from 'utils/time';
import { TitleLineNew } from './CJEventBody/TitleLineNew';
import { getClassroomToDisplayAsString } from 'containers/CahierJournal/DayColumn/Seance/getClassroomsToDisplayAsString';
import useClassroomsForThatDay from 'containers/App/context/ClassroomsContext/useClassroomsForThatDay';
import getDay from 'utils/time/getDay';
import { Density } from 'containers/CahierJournal/useCJConfigurationDisplay';

interface Props {
  event: CJEvent;
  condensed: boolean;
  boLabels: BOLabels;
  classroomsOrg: ClassroomsOrg;
  collapsed: boolean;
  onChange: (v: CJEvent) => void;
  density: Density;
}

export function ClassicEvent(props: Props) {
  const { event, boLabels, condensed, classroomsOrg, density } = props;
  const { start, end } = event;
  const user = useUser();
  const fieldsToDisplay = useFieldsToDisplay();
  const classrooms = useClassroomsForThatDay(getDay(event.start));
  const classroomsToDisplay = getClassroomToDisplayAsString(
    event.classroomIds || [],
    classrooms,
    classroomsOrg,
  );

  const { definitions: customFieldDefinitions } =
    useEventCustomFieldDefinitionsContext();

  return (
    <>
      {event.tag && fieldsToDisplay?.includes('tags') ? (
        <div
          data-testid="cj-seance-tag-title"
          css={[
            bgColors(event.tag.color),
            getTextColorFromBackgroundColor(event.tag.color),
            (event?.tag.color === 'white' ||
              event?.tag.color === 'gris-supershy') &&
              tw`border-b border-neutral-quiet`,
            tw`self-start break-words rounded-t-md items-start px-4 py-2 text-sm font-semibold leading-[17px]`,
            density === 'compact' && tw`py-1`,
          ]}
        >
          {event.tag.title}
        </div>
      ) : (
        <div />
      )}
      <div
        css={[
          tw`flex flex-wrap overflow-hidden pt-2 px-4 mb-2`,
          density === 'compact' && tw`pt-1 mb-1`,
        ]}
      >
        <HoraireComponent
          tw="flex-grow text-neutral-quiet"
          start={start}
          end={end}
        />
        {fieldsToDisplay?.includes('duration') && (
          <div tw="text-right">
            {getDurationInHoursAndMinutesBetween(event.start, event.end)}{' '}
            {classroomsToDisplay || null}
          </div>
        )}
      </div>
      <div tw="px-4 flex flex-col justify-between">
        <TitleLineNew
          showStatusToggle={fieldsToDisplay?.includes('status')}
          shouldColorTitle={!fieldsToDisplay?.includes('tags')}
          css={[!props.collapsed && tw`mb-1`]}
          classroomsOrg={classroomsOrg}
          event={event}
        />
        <CJEventBody
          css={[
            event.status === 'done' && tw`opacity-50`,
            density === 'compact' ? tw`pb-1` : tw`pb-2`,
          ]}
          onChange={props.onChange}
          hidden={props.collapsed}
          userId={user.id}
          customFieldDefinitions={customFieldDefinitions}
          print={false}
          event={event}
          boLabels={boLabels}
          isGuest={user.guestMode?.enabled}
          condensed={condensed}
        />
      </div>
    </>
  );
}
